import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Snackbar,
	Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import type { Drawing } from "../../../../models/TemplateDrawing";
import { AssetDrawingsTable } from "./asset-drawings-table";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateDrawings,
	unLinkTemplateDrawings,
} from "@/pages/templates/repositories/TemplatesRepositorie";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	TemplateId: number | null;
	TemplateName: string;
	Title: string;
	AssetName: string;
}

export const AssetDrawingsPicker: React.FC<Props> = ({
	show,
	onHide,
	TemplateId,
	TemplateName,
	Title,
	AssetName,
}) => {
	const {
		state: { templateDrawingsList },
	} = useTemplatesContext();

	const [selectedDrawings, setSelectedDrawings] = useState<Drawing[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (show) {
			setSelectedDrawings(
				templateDrawingsList.filter((drawing) => drawing.IsAssetResource),
			);
		}
	}, [show, templateDrawingsList]);

	const handleSelectionChanged = (newSelectedDrawings: Drawing[]) => {
		setSelectedDrawings(newSelectedDrawings);
	};

	const getNewlyAddedDrawings = (
		currentSelected: Drawing[],
		originalList: Drawing[],
	): Drawing[] => {
		return currentSelected.filter(
			(selectedDrawing) =>
				!originalList.some(
					(existingDrawing) =>
						existingDrawing.DocumentName === selectedDrawing.DocumentName &&
						existingDrawing.IsAssetResource,
				),
		);
	};

	const getRemovedDrawings = (
		currentSelected: Drawing[],
		originalList: Drawing[],
	): Drawing[] => {
		return originalList.filter(
			(originalDrawing) =>
				originalDrawing.IsAssetResource &&
				!currentSelected.some(
					(selectedDrawing) =>
						selectedDrawing.DocumentName === originalDrawing.DocumentName,
				),
		);
	};

	const handleSave = async () => {
		const newlyAddedDrawings = getNewlyAddedDrawings(
			selectedDrawings,
			templateDrawingsList,
		);
		const removedDrawings = getRemovedDrawings(
			selectedDrawings,
			templateDrawingsList,
		);

		if (newlyAddedDrawings.length > 0) {
			const newAssetDrawings = newlyAddedDrawings.map((drawing) => ({
				DocumentName: drawing.DocumentName,
				IsAssetResource: true,
			}));
			await linkTemplateDrawings(TemplateId as number, newAssetDrawings as any);
			onHide(true);
		} else onHide(false);

		if (removedDrawings.length > 0) {
			const assetDrawingsToRemoved = removedDrawings.map((drawing) => ({
				DocumentName: drawing.DocumentName,
				IsAssetResource: true,
			}));
			await unLinkTemplateDrawings(
				TemplateId as number,
				assetDrawingsToRemoved as any,
			);
			onHide(true);
		} else onHide(false);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						ASSET RESOURCES
					</Typography>
					<Typography
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "1.3rem",
							flexWrap: "wrap",
							gap: "8px",
							padding: "8px 0 0",
						}}
					>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Template:</span>
							<span style={{ marginLeft: "4px" }}>{TemplateName}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Title:</span>
							<span style={{ marginLeft: "4px" }}>{Title}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Asset:</span>
							<span style={{ marginLeft: "4px" }}>{AssetName}</span>
						</div>
					</Typography>
				</DialogTitle>
				<DialogContent>
					<AssetDrawingsTable
						templateDrawings={templateDrawingsList.filter(
							(drawing) => drawing.IsAssetResource,
						)}
						onSelectionChanged={handleSelectionChanged}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item>
							<Button variant="contained" color="primary" onClick={handleSave}>
								Save
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
